import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { getMultUrl, setNewWindowUrl, tencentDocOpenUrlReload } from '@tencent/tencent_doc_open_url';
import { getUrlParameter, removeURLParameter } from '@src/slide/infrastructure/module/independent/url/param-operation';

import { CUSTOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import ua from '@tencent/docs-user-agent';
import { SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IDocRecoverService } from './doc-recover-service.interface';
import { isVipByFetch } from '@src/js/common/ark-extensions/dc-components/vip-linktext';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

/**
 *
 * @param {string} type  可选项 ADD MODIFY
 * @param {object} data
 *  {
        domainId: data.doc_id.domain_id,
        localPadId: data.doc_id.pad_id,
        padType: type === 0? 'doc': 'sheet',
        title: title,
        url: data.doc_url,
        item_type: 1, // item_type 1=我创建的 2=我浏览的 3=分享给我的
    }
 */
function setLocalActions(type, data) {
    data.createdDate = +(new Date());

    const localData = userLocalStorage.getItem(LocalStorageKeys.DOCS_ACTIONS, []);

    const mappings = {}; // 使用mapping做去重
    localData.forEach((item, i) => {
        mappings[`${item.localPadId}$${item.domainId}`] = {
            index: i,
            value: item,
        };
    });
    const obj = mappings[`${data.localPadId}$${data.domainId}`];
    // 不存在则添加上去
    if (!obj) {
        localData.push({
            type: 'ADD',
            data,
        });
    } else {
        // 存在则更新, 因为push是往后面加，所以这里的index还是一致的
        localData[obj.index] = {
            type: getObjType(obj, type),
            data: obj.value,
        };
    }
    userLocalStorage.setItem(LocalStorageKeys.DOCS_ACTIONS, localData);
}

export function getObjType(obj, type) {
    return obj.value === 'ADD' && type === 'MODIFY' ? 'ADD' : type;
}

export class DocRecoverService implements IDocRecoverService {
    private isFromTrash = false;
    private hasClickRecover = false;
    private showDialogCallback: () => void | undefined;

    public initialize() {
        const fromTrash = getUrlParameter('fromTrash');
        if (fromTrash !== null) {
            this.isFromTrash = true;
            const url = getMultUrl(removeURLParameter(document.location.href, 'fromTrash'));
            history.replaceState(null, '', url); // 从url上删除这个参数
        }

        const deleteDocCb = () => {
            // 还原时还会收到一次这个通知，这里做屏蔽
            if (this.hasClickRecover) {
                return;
            }

            if (this.isOwner() && !config.supportEnterprise(EnterPriseType.ALL)) {
                this.showDialogCallback?.();
            } else {
                tencentDocOpenUrlReload();
            }
        };

        onSlideAppReady(this.onSlideAppReady);
        SlideDataServices.SlideAppService.promise().then(() => {
            SlideDataServices.SDKDataServices.EventManager.promise()
                .then(({ socketEvents }) => socketEvents.onDeleteDoc(deleteDocCb));
        });
    }

    public setShowDialogCallback(callback) {
        this.showDialogCallback = callback;
    }

    public async recover(success, error) {
        // 设置aid
        const { setGlobalAid, POSITIONS } = await import('@tencent/docs-scenario-components-aid-globle');
        const isVip = await isVipByFetch();
        const apprec1 = isVip ? 'restore90' : 'restore';
        setGlobalAid({
            position: POSITIONS.DETAIL,
            feature: 'trash',
            apprec1,
            apprec2: 'slide',
        });
        this.hasClickRecover = true;
        // eslint-disable-next-line no-undef
        const { domainId = '', padId: localPadId = '', padType = '' } = SlideDataServices.DocInfoManager() ?? {};
        const { title = '' } = SlideDataServices.SlideTitleManager() ?? {};
        const apiService = await OnlineEditServices.OnlineApiService.promise();
        apiService.recoverDoc(domainId, localPadId).then((response) => {
            const res = response.data;
            if (res.retcode === 0 && res.folder_info && res.folder_info.folder_id) {
                success?.();
                if (this.isFromTrash) {
                    setLocalActions('RECOVER', {
                        domainId,
                        localPadId,
                        padType,
                        title,
                        url: window.location.href.split('#')[0],
                        list_item_type: 1,
                        parent_folder_id: res.folder_info.folder_id,
                    });
                }
                setTimeout(() => {
                    tencentDocOpenUrlReload();
                }, 2000);
            } else {
                error?.(res.retcode);
            }
        })
            .catch((err) => {
                error?.(err.retcode);
            });
    }

    public gotoTrash() {
        // 新的 flutter 独立 app 列表页改为原生版本，需要跳转到原生
        if (ua.isTencentDocsApp && ua.isTencentDocsAppVersionAboveOrEqual('2.2.0')) {
            window.mqq?.invoke('QQApi', 'jumpNativeList', { target: 'Trash' });
            return;
        }

        // 小程序直接返回列表页
        if (ua.isMiniProgram) {
            window.wx?.miniProgram.navigateBack();
            return;
        }

        // 手机QQ 或 TIM
        if (this.isFromTrash && typeof window.tdocs?.popBack === 'function') {
            window.tdocs?.popBack();
            return;
        }

        // 跳转 web 回收站，PC 端、mobile 端会自动重定向对应路径
        setNewWindowUrl(window, CUSTOM.LINK.RECYCLE);
    }

    private onSlideAppReady = () => {
        if (this.isOwner() && this.isDeleted()) {
            this.showDialogCallback?.();
        }
    };

    private isOwner() {
        return SlideDataServices.DocInfoManager()?.isOwner;
    }

    private isDeleted() {
        return SlideDataServices.DocInfoManager()?.isDeleted;
    }
}
